import {Box, Card, CircularProgress, Link, Stack} from '@mui/material';
import {
    MaterialReactTable,
    type MRT_ColumnDef,
    type MRT_PaginationState,
    type MRT_SortingState,
    useMaterialReactTable
} from 'material-react-table';
import type * as React from 'react';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useQuery} from '@tanstack/react-query';
import {getStatementsSearch} from '@/hooks/getStatementsSearch.ts';
import type {Statement} from '@/types/Statement.ts';
import {useFormContext} from "react-hook-form";
import type {StatementSearchSchema} from "@/components/StatementSearchForm/StatementSearchForm.tsx";

const StatementSearchResultsTable = () : React.ReactElement => {
    const {getValues, formState} = useFormContext();
    const formValues = getValues() as StatementSearchSchema;
    const [submittedValues, setSubmittedValues] = useState<StatementSearchSchema>(formValues)

    const [pagination, setPagination] = useState<MRT_PaginationState>({
        pageIndex: 0,
        pageSize: 10,
    });

    const [sorting, setSorting] = useState<MRT_SortingState>([
        {id: 'charge_date', desc: true},
        {id: 'dept_id_lk', desc: false},
        {id: 'fund_lk', desc: false},
    ]);

    const queryFn = useCallback(async () => {
        return await getStatementsSearch(submittedValues, pagination, sorting)
    }, [submittedValues, pagination, sorting])


    const {refetch, isError, error, data, isFetching } =
        useQuery({
            queryKey: ['statements', submittedValues],
            queryFn: () => queryFn(),
            enabled: false,
        }
    )

    useEffect(() => {
        if (formState.isSubmitted && formState.isSubmitSuccessful) {
            console.info('do refetch');
            setSubmittedValues(formValues);
        }
    }, [formState]);

    useEffect(() => {
        if (data || formState.isSubmitted) {
            void refetch();
        }
    }, [pagination, sorting, formState]);

    const columns = useMemo<Array<MRT_ColumnDef<Statement>>>(
        () => [
            {
                accessorKey: 'businessUnit',
                header: 'BU',
                size: 75,
            },
            {
                accessorKey: 'bfsAcct',
                header: 'BFS Acct',
                size: 115,
            },
            {
                accessorKey: 'fund',
                header: 'Fund',
                size: 85,
            },
            {
                accessorKey: 'deptId',
                header: 'Dept ID',
                size: 110,
            },
            {
                accessorKey: 'program',
                header: 'Program',
                size: 110,
            },
            {
                accessorKey: 'cf1',
                header: 'CF1',
                size: 90,
            },
            {
                accessorKey: 'cf2',
                header: 'CF2',
                size: 90,
            },
            {
                accessorKey: 'chargeDate',
                header: 'Charge Date',
                size: 140,
            },
            {
                accessorKey: 'amount',
                header: 'Amount',
                size: 110,
            },
            {
                accessorKey: 'link',
                header: 'Statement Link',
                Cell: ({row}) => (
                    <Link href={row.original?.link} target='_blank'>
                        {row.original?.link}
                    </Link>
                ),
            },
        ],
        [],
    );

    const table = useMaterialReactTable({
        columns: columns,
        data: data?.statements ?? [],
        enableColumnActions: false,
        enableColumnFilters: false,
        enableFilterMatchHighlighting: false,
        enableFullScreenToggle: false,
        enableGlobalFilter: false,
        enableHiding: false,
        enablePagination: true,
        enableTopToolbar: false,
        layoutMode: 'grid-no-grow',
        enableDensityToggle: false,
        initialState: {
            density: 'compact',
        },
        manualPagination: true,
        onPaginationChange: setPagination,
        manualSorting: true,
        onSortingChange: setSorting,
        state: {
            pagination: pagination,
            sorting: sorting,
        },
        defaultColumn: {
            muiTableBodyCellProps: {
                sx: {
                    padding: '4px',
                },
            },
        },
        muiTablePaperProps: {
            elevation: 0,
        },
        rowCount: data?.dataInfo.foundCount ?? 0,
    });

    return <Card sx={{mt: 2, p: 1}}>
        <Stack
            sx={{
                flexDirection: 'row',
                width: '100%',
                justifyContent: 'space-between',
                mb: 2,
            }}
        >
            <Box
                sx={{
                    flexBasis: '100%',
                }}
            ></Box>
            <Box
                sx={{
                    fontSize: '24px',
                    flexBasis: '100%',
                    textAlign: 'center',
                }}
            >
                Statement Search Results
            </Box>
            <Stack
                sx={{
                    flexBasis: '100%',
                    justifyContent: 'center',
                    alignItems: 'flex-end',
                }}
            >
                {isFetching && <Box><CircularProgress size={30}/></Box>}
                {isError && <Box sx={{color: 'darkred'}}>Error: {error.message}</Box>}
            </Stack>
        </Stack>
        <MaterialReactTable table={table}/>
    </Card>;
};

export default StatementSearchResultsTable;
